import React from "react";
import { TFunction } from "i18next";
import { CheckboxIcon } from "@aureskonnect/react-ui";
import { toast } from "react-toastify";
import { uuid } from "uuidv4";

import { getData } from "@helpers/general";
import { CESAR_LOYALTY } from "@constants/index";
import ErrorToast from "@components/Common/ErrorTost";

import { buildFidelityPayload, getShopField, saleModeData } from "../helpers";
import { store } from "../../../../redux/store";
import { setIsLoading, setKey } from "../../../../redux/slices/fidelitySlice";

export function shopHasActiveFidelity(
    shopsData: ShopsFidelityData,
    row: FidelityRow
): boolean {
    const matchedShop = shopsData?.data?.find(
        (el) =>
            (row.fidelityChoiceIndex === CESAR_LOYALTY &&
                el.shopId === row.affectedShop) ||
            (row.fidelityChoiceIndex && el.shopId === row.shopId)
    );
    return (matchedShop?.activeFidelity?.length ?? 0) > 0;
}

export function flattenFidelitiesFromShops(
    selectedShopsRows: ShopsFidelityShop[]
): FidelityRow[] {
    const local: FidelityRow[] = [];
    for (const el of selectedShopsRows) {
        if (el?.subRows !== undefined) {
            for (const element of el.subRows) {
                local.push(element);
            }
        }
    }
    return local;
}

export function hasStateChanged(
    fidelities: FidelityRow[],
    selectedRowsFidelity: FidelityRow | null | undefined,
    activeFidelity: FidelityActiveItem[]
): boolean {
    if (!selectedRowsFidelity) return false;
    for (const el of fidelities) {
        if (selectedRowsFidelity.Id === el.Id) {
            const match = activeFidelity.find(
                (element) => element.Id === el.Id
            );
            if (match && match.state !== el.isActive) return true;
        }
    }
    return false;
}

export function pickDefaultFidelity(
    rowsShop: ShopsFidelityShop[],
    localShop: string[]
): string {
    if (localShop.length > 1) return "";
    if (rowsShop[0]?.subRows !== undefined) {
        return rowsShop[0].subRows[0]?.Id ?? rowsShop[0].Id;
    }
    if (rowsShop[1]?.subRows !== undefined) {
        return rowsShop[1].subRows[1]?.Id ?? rowsShop[0].Id;
    }
    return "";
}

export function unarchiveForShops(
    isArchived: FidelityArchiveEntry[],
    localShop: string[]
): FidelityArchiveEntry[] {
    for (const el of localShop) {
        const idx = isArchived.findIndex((elem) => elem.shopId === el);
        if (idx === -1) {
            isArchived.push({ shopId: el, status: false });
        } else {
            isArchived[idx].status = false;
        }
    }
    return isArchived;
}

export function buildAssociatePayload(
    selectedRowsFidelity: FidelityRow,
    saleModes: SaleModeVisibility[],
    activeFidelity: FidelityActiveItem[],
    isArchived: FidelityArchiveEntry[] | boolean,
    shopId: string,
    t: TFunction
): FidelityPayload {
    const payload = buildFidelityPayload(selectedRowsFidelity, t);
    payload.sortedVisibility = saleModes;
    payload.isActive =
        activeFidelity.find((el) => el.Id === selectedRowsFidelity.Id)?.state ??
        false;
    payload.isArchived =
        selectedRowsFidelity.fidelityChoiceIndex === CESAR_LOYALTY
            ? JSON.stringify(isArchived)
            : false;
    payload.shopId = shopId;
    return payload;
}

export interface AssociatePayloadResult {
    objectData: Record<string, FidelityPayload>;
    shopIds: string[];
    isError: boolean;
    haveOne: boolean;
}

export function prepareAssociatePayload(
    localShop: string[],
    rowsShop: ShopsFidelityShop[],
    selectedRowsFidelity: FidelityRow,
    activeFidelity: FidelityActiveItem[],
    saleModes: SaleModeVisibility[],
    fidelity: string,
    t: TFunction
): AssociatePayloadResult {
    const objectData: Record<string, FidelityPayload> = {};
    const shopIds: string[] = [];
    let isError = false;
    let haveOne = false;
    let isArchived: FidelityArchiveEntry[] | boolean =
        selectedRowsFidelity.fidelityChoiceIndex === CESAR_LOYALTY
            ? JSON.parse(JSON.stringify(selectedRowsFidelity.isArchived))
            : false;

    for (const element of localShop) {
        const associated = getShopField(rowsShop, element, "subRows");
        const archived = getShopField(rowsShop, element, "archivedFidelity");
        if (fidelity === "") continue;

        const outcome = classifyShopForAssociation(
            associated,
            archived,
            selectedRowsFidelity,
            activeFidelity
        );

        if (outcome === "skip-already-assigned") {
            haveOne = true;
            continue;
        }
        if (outcome === "error-other-fidelity") {
            isError = true;
            continue;
        }
        if (outcome === "associate") {
            if (Array.isArray(isArchived)) {
                unarchiveForShops(isArchived, localShop);
            }
            objectData[selectedRowsFidelity.Id] = buildAssociatePayload(
                selectedRowsFidelity,
                saleModes,
                activeFidelity,
                isArchived,
                element,
                t
            );
            if (!shopIds.includes(element)) {
                shopIds.push(element);
            }
        }
    }

    return { objectData, shopIds, isError, haveOne };
}

export function classifyShopForAssociation(
    associated: FidelityRow[],
    archived: FidelityRow[],
    selectedRowsFidelity: FidelityRow,
    activeFidelity: FidelityActiveItem[]
): "skip-already-assigned" | "error-other-fidelity" | "associate" | "noop" {
    const hasOtherCesar = (JSON.parse(
        JSON.stringify(archived)
    ) as FidelityRow[]).find(
        (el) =>
            el.Id !== selectedRowsFidelity.Id &&
            selectedRowsFidelity.fidelityChoiceIndex === CESAR_LOYALTY &&
            el.fidelityChoiceIndex === CESAR_LOYALTY
    );
    if (hasOtherCesar) return "skip-already-assigned";

    const isActive =
        activeFidelity.find((elt) => elt.Id === selectedRowsFidelity.Id)
            ?.state ?? false;

    const indexSameId = associated.findIndex(
        (elt) => elt.Id === selectedRowsFidelity.Id
    );
    if (indexSameId === -1 && associated.length !== 0) {
        return "error-other-fidelity";
    }

    const indexSameIdDiffActive = associated.findIndex(
        (elt) => elt.Id === selectedRowsFidelity.Id && elt.isActive !== isActive
    );
    const indexSameIdSameActive = associated.findIndex(
        (elt) => elt.Id === selectedRowsFidelity.Id && elt.isActive === isActive
    );
    if (
        indexSameIdDiffActive !== -1 ||
        associated.length === 0 ||
        indexSameIdSameActive === -1
    ) {
        return "associate";
    }

    return "noop";
}

export interface ActiveFidelityDeps {
    row: FidelityRow;
    userID: number;
    franchiseID: string;
    operatorID: string;
    uuidUser: string | null;
    fetchGlobal: FetchGlobal;
    t: TFunction;
    setDataIsUpdated: React.Dispatch<React.SetStateAction<boolean | number>>;
}

export async function activeShopFidelity(
    deps: ActiveFidelityDeps
): Promise<void> {
    const {
        row,
        userID,
        franchiseID,
        operatorID,
        uuidUser,
        fetchGlobal,
        t,
    } = deps;
    store.dispatch(setIsLoading(true));
    let nextIsArchived = row.isArchived;
    if (
        row.fidelityChoiceIndex === CESAR_LOYALTY &&
        Array.isArray(row.isArchived)
    ) {
        const index = row.isArchived.findIndex(
            (shop: FidelityArchiveEntry) =>
                shop.shopId === `${row.affectedShop}` ||
                shop.shopId === row.affectedShop
        );
        if (index !== -1) {
            nextIsArchived = row.isArchived.map(
                (shop: FidelityArchiveEntry, i: number) =>
                    i === index ? { ...shop, status: false } : shop
            );
        }
    }
    toast.dismiss();

    const payload = buildFidelityPayload(row, t);
    payload.isArchived =
        row.fidelityChoiceIndex !== CESAR_LOYALTY
            ? false
            : JSON.stringify(nextIsArchived);
    payload.shopId =
        row.fidelityChoiceIndex === CESAR_LOYALTY
            ? String(row.affectedShop)
            : row.shopId.toString();

    const dataConfig = {
        userId: userID,
        shopId: [
            row.fidelityChoiceIndex === CESAR_LOYALTY
                ? row.affectedShop
                : row.shopId,
        ],
        franchiseId: franchiseID,
        operatorId: operatorID,
        uuidUser: uuidUser,
        isShop: true,
        data: { [row.Id]: payload },
        add: true,
    };

    const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity`;
    try {
        const result: any = await fetchGlobal(apiUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dataConfig),
        });
        if (result.error) {
            throw Error("error");
        }
        store.dispatch(setIsLoading(false));
        toast.success(
            `${t("The fidelity setting has been successfully unarchived")!}`,
            {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            }
        );
        store.dispatch(setKey(uuid()));
        await getData(
            franchiseID,
            uuidUser as string,
            row.affectedShop,
            fetchGlobal
        );
    } catch (err) {
        store.dispatch(setIsLoading(false));
        toast.error(`${t("There's an error")!}`, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    }
}

export interface AssociateFidelityDeps {
    userID: number;
    franchiseID: string;
    operatorID: string;
    shopID: string;
    uuidUser: string | null;
    localShop: string[];
    rowsShop: ShopsFidelityShop[];
    selectedRowsFidelity: FidelityRow;
    activeFidelity: FidelityActiveItem[];
    fidelity: string;
    isAssociateModalClosed: boolean;

    fetchGlobal: FetchGlobal;
    t: TFunction;
    setIsLoading: (v: boolean) => void;
    setIsValidateButtonEnabled: (v: boolean) => void;
    setLimit: (v: number) => void;
    setIsAssociateModalOpened: (v: boolean) => void;
    setIsAssociateModalClosed: (v: boolean) => void;
    setKey: (v: string) => void;
}

export async function runAssociateFidelity(
    deps: AssociateFidelityDeps
): Promise<void> {
    const {
        userID,
        franchiseID,
        operatorID,
        shopID,
        uuidUser,
        localShop,
        rowsShop,
        selectedRowsFidelity,
        activeFidelity,
        fidelity,
        isAssociateModalClosed,
        fetchGlobal,
        t,
        setIsValidateButtonEnabled,
        setLimit,
        setKey,
        setIsAssociateModalOpened,
        setIsAssociateModalClosed,
    } = deps;

    setIsLoading(true);
    let isFidelityAffected = false;
    setLimit(1);
    setIsValidateButtonEnabled(false);

    try {
        const warningMessage = t(
            "Please note that one or more fidelity settings could not be assigned. A store cannot have more than one loyalty setting."
        );
        const saleModesResult = await saleModeData(
            userID,
            franchiseID,
            shopID,
            fetchGlobal
        );

        const saleModes = saleModesResult.ok ? saleModesResult.value : [];

        const {
            objectData,
            shopIds,
            isError,
            haveOne,
        } = prepareAssociatePayload(
            localShop,
            rowsShop,
            selectedRowsFidelity,
            activeFidelity,
            saleModes,
            fidelity,
            t
        );

        if (Object.keys(objectData).length === 0) {
            if (haveOne) {
                setIsValidateButtonEnabled(true);
                ErrorToast(warningMessage);
                setLimit(2);
                setIsAssociateModalOpened(false);
            }
            return;
        }

        const dataFidelity = {
            userId: userID,
            shopId: shopIds,
            franchiseId: franchiseID,
            operatorId: operatorID,
            uuidUser: uuidUser,
            data: objectData,
            affectation: true,
        };

        const data: any = await fetchGlobal(
            `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dataFidelity),
            }
        );

        if (data.error) {
            throw Error("error");
        }

        if (data.error === false) {
            if (isError) ErrorToast(warningMessage);

            if (haveOne) {
                setIsValidateButtonEnabled(true);
                ErrorToast(warningMessage);
                setLimit(2);
            }

            if (Object.keys(objectData).length > 0) {
                isFidelityAffected = true;
                toast.success(
                    `${t("Fidelity assignment was successfully assigned")!}`,
                    {
                        position: toast.POSITION.TOP_CENTER,
                        theme: "colored",
                        icon: (
                            <CheckboxIcon height={25} width={25} fill="white" />
                        ),
                    }
                );
                setKey(uuid());
            }
        }

        setIsAssociateModalOpened(false);
        setIsAssociateModalClosed(!isAssociateModalClosed);

        if (isFidelityAffected) {
            setTimeout(async () => {
                await getData(
                    franchiseID,
                    uuidUser as string,
                    shopID,
                    fetchGlobal
                );
            }, 1000);
        }
    } catch (e) {
        toast.error(`${t("There's an error")!}`, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    } finally {
        setIsLoading(false);
    }
}

export function extractIds(
    source: { data?: Array<{ Id: string }> } | undefined
): string[] {
    const ids: string[] = [];
    if (source?.data !== undefined) {
        for (const el of source.data) {
            ids.push(el.Id);
        }
    }
    return [...new Set(ids)];
}

export function hasDuplicateShops(arrayOfRows: FidelityRow[]): boolean {
    const seen: Record<string, boolean> = {};
    for (const el of arrayOfRows) {
        const key = String(
            el.fidelityChoiceIndex === CESAR_LOYALTY
                ? el.affectedShop
                : el.shopId
        );
        if (seen[key]) return true;
        seen[key] = true;
    }
    return false;
}

type UnarchiveFidelitiesParams = {
    arrayOfRows: FidelityRow[];
    userID: number;
    franchiseID: string;
    operatorID: string;
    uuidUser: string | null;
    fetchGlobal: FetchGlobal;
    t: TFunction;
};

export async function unarchiveFidelities(
    params: UnarchiveFidelitiesParams
): Promise<Result<void>> {
    const {
        arrayOfRows,
        userID,
        franchiseID,
        operatorID,
        uuidUser,
        fetchGlobal,
        t,
    } = params;

    store.dispatch(setIsLoading(true));

    const idsShops = arrayOfRows
        .filter((el) => el.fidelityChoiceIndex === CESAR_LOYALTY)
        .map((el) => el.affectedShop);
    const rows: FidelityRow[] = arrayOfRows.map((el) => {
        if (
            el.fidelityChoiceIndex === CESAR_LOYALTY &&
            Array.isArray(el.isArchived)
        ) {
            return {
                ...el,
                isArchived: (el.isArchived as FidelityArchiveEntry[]).map(
                    (shop) =>
                        idsShops.includes(shop.shopId)
                            ? { ...shop, status: false }
                            : shop
                ),
            };
        }
        return el;
    });

    const data: Record<string, FidelityPayload> = {};
    const shopsIdArchived: string[] = [];
    for (const el of rows) {
        const id = el.shopId?.toString();
        if (id && !shopsIdArchived.includes(id)) {
            shopsIdArchived.push(id);
        }
        const payload = buildFidelityPayload(el, t);
        payload.isArchived =
            el.fidelityChoiceIndex !== CESAR_LOYALTY
                ? false
                : JSON.stringify(el.isArchived);
        data[el.Id] = payload;
    }

    const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity`;
    try {
        await fetchGlobal(apiUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                userId: userID,
                shopId: shopsIdArchived,
                franchiseId: franchiseID,
                operatorId: operatorID,
                uuidUser,
                isShop: false,
                data,
                add: true,
            }),
        });
        toast.success(
            `${t("The fidelity setting has been successfully unarchive")!}`,
            {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            }
        );
        return { ok: true, value: undefined };
    } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        toast.error(err.message, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
        return { ok: false, error: err };
    } finally {
        store.dispatch(setIsLoading(false));
    }
}
